// Reading an EIP-1193 provider rejection without destroying it.
//
// ─── Why this file exists ─────────────────────────────────────────────────
//
// On 2026-09-07 a physical acceptance attempt failed with SIGNER_B on Base
// mainnet. The console reported:
//
//     Signature verification failed
//     [object Object]
//
// Both halves were wrong. Nothing had been verified — the wallet had refused to
// sign, so no request ever reached the server — and the reason it refused was
// destroyed on the way to the screen.
//
// The destruction was `String(e)`. EIP-1193 providers reject with a PLAIN
// OBJECT, `{ code, message, data }`, not an `Error`. Every error the console
// raises itself is an `Error` subclass, so a `String(e)` fallback looks
// harmless right up to the moment a provider rejects — and then it converts the
// single most diagnostic value in the whole flow into eight useless characters.
//
// So provider rejections get read, not stringified.
//
// ─── What is deliberately NOT copied out ──────────────────────────────────
//
// `data` is provider-defined and unbounded. It is described rather than
// reproduced: a primitive is passed through truncated, an object contributes
// its KEY NAMES and its nested `message` (the field wallets actually put the
// cause in), and nothing else. That keeps the useful signal without turning a
// debug bundle an operator pastes into a chat window into an exfiltration path
// for whatever a wallet decided to attach.

/** EIP-1193 / JSON-RPC codes worth naming. Anything else stays `provider_error`. */
const USER_REJECTED = 4001;
const UNAUTHORIZED = 4100;
const UNSUPPORTED_METHOD = 4200;
const DISCONNECTED = 4900;
const CHAIN_DISCONNECTED = 4901;

export type ProviderFailureKind =
  | "user_rejected"
  | "unauthorized"
  | "unsupported_method"
  | "disconnected"
  | "provider_error";

export interface ProviderFailure {
  kind: ProviderFailureKind;
  /** The EIP-1193 code, when the provider supplied one. */
  code: number | null;
  /** The provider's own message, truncated. */
  message: string | null;
  /** A SAFE description of `data` — never the raw value. Null when absent. */
  data: string | null;
  /** One line for the banner. Always non-empty. */
  summary: string;
}

const MAX_MESSAGE = 300;
const MAX_DATA = 300;
const MAX_KEYS = 6;

function truncate(s: string, max: number): string {
  const t = s.trim();
  return t.length <= max ? t : `${t.slice(0, max)}… (${t.length} chars)`;
}

/** A numeric code, whether the provider sent a number or a numeric string. */
function readCode(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && /^-?\d+$/.test(v.trim())) return Number.parseInt(v.trim(), 10);
  return null;
}

/**
 * Describe `data` without reproducing it.
 *
 * A primitive is the value itself (truncated). An object contributes its key
 * names plus a nested `message` if there is one — wallets routinely bury the
 * real cause at `data.message` while the outer message says something generic.
 */
function describeData(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "string") return truncate(v, MAX_DATA);
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return `array(${v.length})`;
  if (typeof v === "object") {
    const keys = Object.keys(v as Record<string, unknown>);
    const shown = keys.slice(0, MAX_KEYS).join(", ");
    const more = keys.length > MAX_KEYS ? `, +${keys.length - MAX_KEYS} more` : "";
    const nested = (v as Record<string, unknown>)["message"];
    const nestedPart =
      typeof nested === "string" && nested.trim() !== ""
        ? ` message="${truncate(nested, MAX_DATA)}"`
        : "";
    return `object{${shown}${more}}${nestedPart}`;
  }
  return typeof v;
}

function summarize(kind: ProviderFailureKind, code: number | null, message: string | null): string {
  const tail = message !== null && message !== "" ? `: ${message}` : "";
  const codePart = code !== null ? ` (code ${code})` : "";
  switch (kind) {
    case "user_rejected":
      return `You declined the signature request in your wallet${codePart}.`;
    case "unauthorized":
      return `The wallet has not authorized this site to request a signature${codePart}${tail}`;
    case "unsupported_method":
      return `The wallet does not support personal_sign${codePart}${tail}`;
    case "disconnected":
      return `The wallet is disconnected${codePart}${tail}`;
    default:
      return `The wallet refused the signature request${codePart}${tail}`;
  }
}

function classify(code: number | null): ProviderFailureKind {
  switch (code) {
    case USER_REJECTED:
      return "user_rejected";
    case UNAUTHORIZED:
      return "unauthorized";
    case UNSUPPORTED_METHOD:
      return "unsupported_method";
    case DISCONNECTED:
    case CHAIN_DISCONNECTED:
      return "disconnected";
    default:
      return "provider_error";
  }
}

/**
 * Read an EIP-1193 rejection, or return null if this is not one.
 *
 * Null means "not provider-shaped" and tells the caller to fall through to its
 * ordinary `Error` handling. Provider-shaped means: an object carrying a usable
 * `code`, or a `message` when the throw is not an `Error` at all — the second
 * clause matters because a wallet may reject with `{ message }` and no code,
 * and that is still infinitely better than `[object Object]`.
 *
 * MetaMask nests: the outer object often carries a generic message while the
 * cause sits at `data.originalError` or `cause`. Both are unwrapped, once.
 */
export function describeProviderError(e: unknown): ProviderFailure | null {
  if (typeof e !== "object" || e === null) return null;
  const o = e as Record<string, unknown>;

  // Unwrap ONE level of the usual nestings.
  //
  // The outer layer having a code is NOT a reason to stop. MetaMask's real
  // shape for a declined signature is an outer `-32603 Internal JSON-RPC error`
  // wrapping `data.originalError = { code: 4001 }` — so an unwrap that only ran
  // when the outer code was missing would report "internal error" for a user
  // who simply pressed Reject. A nested EIP-1193 code therefore WINS over a
  // generic outer one; a nested code outside that range does not, because then
  // the outer layer is the more specific statement.
  const nestedCandidates = [
    (o["data"] as Record<string, unknown> | undefined)?.["originalError"],
    o["cause"],
  ];
  let src = o;
  const outerCode = readCode(o["code"]);
  for (const n of nestedCandidates) {
    if (typeof n !== "object" || n === null) continue;
    const nestedCode = readCode((n as Record<string, unknown>)["code"]);
    if (nestedCode === null) continue;
    const nestedIsEip1193 = classify(nestedCode) !== "provider_error";
    if (outerCode === null || nestedIsEip1193) {
      src = n as Record<string, unknown>;
      break;
    }
  }

  const code = readCode(src["code"]);
  const rawMessage = typeof src["message"] === "string" ? src["message"] : null;

  // Not provider-shaped: no code, and either an Error (whose own handling is
  // better) or nothing useful to say.
  if (code === null && (e instanceof Error || rawMessage === null || rawMessage.trim() === "")) {
    return null;
  }

  const message = rawMessage !== null ? truncate(rawMessage, MAX_MESSAGE) : null;
  const kind = classify(code);
  return {
    kind,
    code,
    message,
    data: describeData(src["data"] ?? o["data"]),
    summary: summarize(kind, code, message),
  };
}
